"use client";

import { IconAlertInfoOutline18, IconCheckOutline18 } from "nucleo-ui-outline-18";
import { TokenIcon } from "@/components/ui/token-icon";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useEffect, useMemo, useState } from "react";
import type { TriggerArchetypeProps } from "./types";
const CACHE_TTL_MS = 30_000;

/** Module-level price cache keyed by asset symbol. */
const priceCache = new Map<string, {
  price: string;
  fetchedAt: number;
}>();

/** Compute a test price that satisfies the trigger condition. */
function getPriceThatTriggers(targetPrice: number, operation?: string): number {
  // Use 10% of target for sub-dollar assets (e.g., PEPE at $0.00001) to avoid negative/zero values
  const increment = targetPrice > 0 && targetPrice < 1 ? targetPrice * 0.1 : 1;
  switch (operation) {
    case ">":
      return targetPrice + increment;
    case ">=":
    case "==":
    case "<=":
      return targetPrice;
    case "<":
      return Math.max(0, targetPrice - increment);
    default:
      return targetPrice + increment;
  }
}

/** Evaluate whether testPrice satisfies the condition against targetPrice. */
function evaluateCondition(testPrice: number, targetPrice: number, op: string): boolean {
  switch (op) {
    case ">":
      return testPrice > targetPrice;
    case ">=":
      return testPrice >= targetPrice;
    case "<":
      return testPrice < targetPrice;
    case "<=":
      return testPrice <= targetPrice;
    case "==":
      return Math.abs(testPrice - targetPrice) < Number.EPSILON * 100;
    default:
      return true;
  }
}

/** Format "BTCUSD" → "BTC/USD" by stripping trailing "USD". */
function formatAssetPair(asset: string): string {
  if (asset.endsWith("USD")) {
    return `${asset.slice(0, -3)}/USD`;
  }
  return asset;
}

/** Format a condition operator for display: ">=" → "≥", "<=" → "≤", "==" → "=" */
function formatOperator(op: string): string {
  switch (op) {
    case ">=":
      return "≥";
    case "<=":
      return "≤";
    case "==":
      return "=";
    default:
      return op;
  }
}
function formatUSD(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: value < 1 ? 8 : 2
  });
}

/** Build a mocked OraclePrice rawResponse matching the Go struct. */
function buildRawResponse(asset: string, testPrice: number): Record<string, unknown> {
  // toFixed(18) avoids scientific notation and guarantees frac is exactly 18 chars.
  const [whole = "0", frac = ""] = testPrice.toFixed(18).split(".");
  const price1e18 = (whole + frac).replace(/^0+/, "") || "0";
  return {
    timestamp: Math.floor(Date.now() / 1000),
    asset_id: asset,
    signature_type: "unsigned",
    trigger: "test",
    price: price1e18,
    stork_signed_price: null,
    signed_prices: []
  };
}

/**
 * TokenPriceAlert archetype — shows the configured asset & condition,
 * fetches the live price, and lets the user set a custom test price.
 *
 * Used for: token-price-cexes
 */
export function TokenPriceAlert({
  triggerPayload,
  onRun
}: TriggerArchetypeProps) {
  const asset = triggerPayload?.asset as string ?? "";
  const operation = triggerPayload?.operation as string ?? ">";
  const targetPrice = triggerPayload?.targetPrice as number ?? 0;
  const [livePrice, setLivePrice] = useState<string | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [livePriceError, setLivePriceError] = useState(false);
  const [priceInput, setPriceInput] = useState(() => String(targetPrice > 0 ? getPriceThatTriggers(targetPrice, operation) : 100));
  const testPrice = useMemo(() => {
    if (priceInput.trim() === "") return null;
    const n = Number(priceInput);
    return isNaN(n) ? null : n;
  }, [priceInput]);
  const conditionMet = useMemo(() => testPrice != null && targetPrice > 0 ? evaluateCondition(testPrice, targetPrice, operation) : null, [testPrice, targetPrice, operation]);
  useEffect(() => {
    if (!asset) return;
    const cached = priceCache.get(asset);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setLivePrice(cached.price);
      setLivePriceLoading(false);
      setLivePriceError(false);
      return;
    }
    let cancelled = false;
    setLivePrice(null);
    setLivePriceLoading(true);
    setLivePriceError(false);
    (async () => {
      try {
        const res = await authenticatedFetch<{
          data: {
            prices: Record<string, string>;
          };
        }>(`${V2_ENDPOINT}/v1/token-price-cexes/latest?assets=${encodeURIComponent(asset)}`);
        if (cancelled) return;
        if (!res.ok) {
          setLivePriceError(true);
          return;
        }
        const json = await res.json();
        const price = json.data?.prices?.[asset];
        if (price) {
          priceCache.set(asset, {
            price,
            fetchedAt: Date.now()
          });
          setLivePrice(price);
        } else {
          setLivePriceError(true);
        }
      } catch {
        if (!cancelled) setLivePriceError(true);
      } finally {
        if (!cancelled) setLivePriceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asset]);

  // Notify parent of current payload — onRun is a stable state setter (setArchetypePayload)
  // from TriggerInputModal, so omitting it from deps is safe and avoids infinite loops.
  useEffect(() => {
    if (testPrice != null) {
      onRun({
        price: testPrice,
        triggeredAt: new Date().toISOString(),
        rawResponse: buildRawResponse(asset, testPrice)
      });
    } else {
      onRun(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testPrice, asset]);
  if (!asset) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800/50 dark:bg-amber-900/20">
        <p className="text-sm text-amber-700 dark:text-amber-300">
          No asset configured. Select an asset on the trigger node to test.
        </p>
      </div>;
  }
  const livePriceNum = livePrice != null ? Number(livePrice) : NaN;
  const livePriceFormatted = !isNaN(livePriceNum) ? formatUSD(livePriceNum) : null;
  return <div className="space-y-3">
      <div className="flex items-center gap-2">
        <TokenIcon symbol={asset} size={24} enableStockTickers />
        <span className="text-ds-text-primary text-sm font-semibold">
          {formatAssetPair(asset)} {formatOperator(operation)} {formatUSD(targetPrice)}
        </span>

        <span className="ml-auto flex items-center gap-1.5">
          {livePriceLoading ? <>
              <PixelLoader size="xs" color="blue" />
              <span className="label-xs">Loading...</span>
            </> : livePriceError || !livePriceFormatted ? <span className="label-xs">Live price unavailable</span> : <>
              <span className="label-xs">Live:</span>
              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{livePriceFormatted}</span>
            </>}
        </span>
      </div>
      <div className="pt-2">
        <label htmlFor="token-price-test" className="field-label mb-1 block">
          Simulate price
        </label>
        <div className="flex items-center gap-2">
          <input id="token-price-test" type="text" inputMode="decimal" value={priceInput} onChange={e => setPriceInput(e.target.value)} className="border-ds-border-default text-ds-text-primary bg-ds-surface-card w-full rounded-lg border px-2.5 py-1.5 font-mono text-sm" />
          {conditionMet != null && <span className={`flex flex-shrink-0 items-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium ${conditionMet ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-400" : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-400"}`}>
              {conditionMet ? <>
                  <IconCheckOutline18 className="h-3.5 w-3.5" /> Satisfies condition
                </> : <>
                  <IconAlertInfoOutline18 className="h-3.5 w-3.5" /> Does not satisfy
                </>}
            </span>}
        </div>
        <p className="caption-xs mt-1">Auto-set to satisfy trigger condition</p>
      </div>
    </div>;
}